import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { cn } from '../lib/cn.js';

type Variant = 'primary' | 'secondary' | 'danger' | 'ghost';

interface Props extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: 'md' | 'lg' | 'sm';
}

const variantStyles: Record<Variant, string> = {
  primary: 'bg-blue-600 hover:bg-blue-500 active:bg-blue-700 text-white',
  secondary:
    'bg-neutral-800 hover:bg-neutral-700 text-neutral-100 border border-neutral-700',
  danger: 'bg-red-600 hover:bg-red-500 text-white',
  ghost: 'bg-transparent text-neutral-300 hover:bg-neutral-800',
};

export const Button = forwardRef<HTMLButtonElement, Props>(
  ({ className, variant = 'primary', size = 'md', ...rest }, ref) => {
    const sizeCls =
      size === 'lg'
        ? 'h-12 px-5 text-base'
        : size === 'sm'
          ? 'h-9 px-3 text-sm'
          : 'h-11 px-4 text-sm';
    return (
      <button
        ref={ref}
        className={cn(
          'inline-flex items-center justify-center gap-2 rounded-xl font-medium transition-colors',
          'disabled:opacity-50 disabled:cursor-not-allowed select-none',
          'touch-manipulation',
          sizeCls,
          variantStyles[variant],
          className,
        )}
        {...rest}
      />
    );
  },
);
Button.displayName = 'Button';
